import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  REMOTE_ASESETUP_QUESTIONDELETE,
  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN,
  REMOTE_ASESETUP_OPENQUESTIONSORDER,
} from 'store/actions';
import {
  selectAseSetupLibraryAnswer,
  selectAseSetupLibraryQuestion,
  selectAseSetupLibraryQuestionSubItems,
} from 'store/remote/selectors';
import { AssessmentQuestionType } from 'types/types';

type Props = {
  id: number;
  level: number;
};

export const SectionListQuestionListItem: React.FC<Props> = ({ id, level }) => {
  const dispatch = useDispatch();
  const question = useSelector((state) =>
    selectAseSetupLibraryQuestion(state, id),
  )!;
  const answer = useSelector((state) =>
    selectAseSetupLibraryAnswer(state, question.answerId || -1),
  );

  const subitems = useSelector((state) =>
    selectAseSetupLibraryQuestionSubItems(state, id, true),
  );

  const typeLabel =
    question.type === AssessmentQuestionType.checklist
      ? 'Checklist'
      : 'Question';

  const handleOpen = () =>
    dispatch(
      REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
        id,
      }),
    );
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'stretch',
        flex: 1,
      }}
    >
      {!!level &&
        [...Array(level)].map((_, i) => (
          <View
            key={i}
            style={{
              width: 2,
              marginVertical: 2,
              marginLeft: 2,
              backgroundColor: theme.colors.fieldValueText,
            }}
          />
        ))}
      <CardRow
        onPress={handleOpen}
        style={{ flex: 1 }}
        icon={
          question.type === AssessmentQuestionType.checklist
            ? ICONS.CircleCSolid
            : ICONS.CircleQSolid
        }
        iconColor={
          question.type === AssessmentQuestionType.checklist
            ? theme.colors.checklist
            : theme.colors.question
        }
        menuOptions={[
          {
            text: `Open ${typeLabel}`,
            icon: ICONS.CircleInfoLight,
            onPress: () =>
              dispatch(
                REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({ id: question.id }),
              ),
          },
          {
            text: 'Reorder This',
            onPress: () =>
              dispatch(
                REMOTE_ASESETUP_OPENQUESTIONSORDER({
                  sectionId: question.sectionId!,
                  answerId: question.answerId as unknown as undefined,
                }),
              ),
            icon: ICONS.GripLinesLight,
          },
          {
            text: `Delete ${typeLabel}`,
            onPress: () =>
              dispatch(REMOTE_ASESETUP_QUESTIONDELETE({ id: question.id })),
            icon: ICONS.TrashCanLight,
            destructive: true,
          },
          '-',
          question.type === AssessmentQuestionType.question
            ? {
                text: 'New Sub-Checklist',
                onPress: subitems.answerIds.length
                  ? () =>
                      dispatch(
                        REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                          type: AssessmentQuestionType.checklist,
                          parentQuestionId: question.id,
                        }),
                      )
                  : undefined,
                icon: ICONS.PlusLight,
              }
            : null,
          question.type === AssessmentQuestionType.question
            ? {
                text: 'New Sub-Question',
                disabled: !subitems.answerIds.length,
                onPress: subitems.answerIds.length
                  ? () =>
                      dispatch(
                        REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                          type: AssessmentQuestionType.question,
                          parentQuestionId: question.id,
                        }),
                      )
                  : undefined,
                icon: ICONS.PlusLight,
              }
            : null,
        ].filter(filterArrayEmpty)}
        indentActions
      >
        {answer && (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Icon
              source={ICONS.ArrowTurnDownLight}
              size={12}
              color={theme.colors.fieldValueText}
              style={{
                marginBottom: 0,
                marginRight: theme.paddingHorizontal / 2,
                transform: [
                  {
                    rotate: '-90deg',
                  },
                  {
                    scaleX: -1,
                  },
                ],
              }}
            />
            <Txt size="xxxsmall" style={{ color: theme.colors.fieldValueText }}>
              <Txt size="xxxsmall" weight="bold">
                {answer.answer}
              </Txt>
            </Txt>
          </View>
        )}
        <Txt size="small">{question.question}</Txt>
        <Txt size="xxxsmall" style={{ color: theme.colors.fieldValueText }}>
          {question.type === AssessmentQuestionType.question ? (
            <Txt size="xxxsmall">
              {pluralise(
                subitems.answerIds.length,
                '1 Answer',
                '{x} Answers',
                'No Answers',
              )}
            </Txt>
          ) : (
            <Txt size="xxxsmall">
              {pluralise(
                subitems.checklistItemIds.length,
                '1 Checklist',
                '{x} Checklist Items',
                'No Checklist Items',
              )}
            </Txt>
          )}
          {!!subitems.actionIds.length && (
            <>
              {' • '}
              <Txt
                size="xxxsmall"
                style={{ color: theme.colors.assessmentAction }}
              >
                {pluralise(
                  subitems.actionIds.length,
                  '1 Action',
                  '{x} Actions',
                )}
              </Txt>
            </>
          )}
          {!!subitems.controlIds.length && (
            <>
              {' • '}
              <Txt
                size="xxxsmall"
                style={{ color: theme.colors.assessmentControl }}
              >
                {pluralise(
                  subitems.controlIds.length,
                  '1 Control',
                  '{x} Controls',
                )}
              </Txt>
            </>
          )}
        </Txt>
      </CardRow>
    </View>
  );
};
